import React, { useMemo } from "react";
import { AuthProvider } from "react-oauth2-code-pkce";
import { useLocation } from "react-router-dom";

/**
 * Fetch Interceptor for OAuth Token Compatibility
 *
 * This interceptor bridges the compatibility gap between:
 * - WorkOS/Ligonier auth server: Uses custom "pkce" grant_type and camelCase token fields
 * - react-oauth2-code-pkce library: Expects standard OAuth 2.0 format
 *
 * Transformations applied:
 * 1. Request: Changes grant_type from "authorization_code" to "pkce" for initial auth
 * 2. Response: Converts camelCase fields (accessToken) to snake_case (access_token)
 */
if (typeof window !== "undefined" && !window._fetchIntercepted) {
  window._fetchIntercepted = true;
  const originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const [url, options] = args;
    const isTokenEndpoint = url?.includes?.("/token");

    // Transform token requests to use server's expected grant_type
    if (isTokenEndpoint && options?.body && typeof options.body === "string") {
      const params = new URLSearchParams(options.body);

      // Only transform initial PKCE auth requests (not refresh requests)
      if (
        params.get("grant_type") === "authorization_code" &&
        params.has("code") &&
        params.has("code_verifier")
      ) {
        params.set("grant_type", "pkce");
        options.body = params.toString();
      }
    }

    const response = await originalFetch.apply(this, args);

    // Transform token responses from server's camelCase to OAuth standard snake_case
    if (isTokenEndpoint && response.ok) {
      const clonedResponse = response.clone();

      try {
        const data = await clonedResponse.json();

        // Only transform if response uses camelCase format
        if (data.accessToken || data.refreshToken) {
          const transformedData = {
            access_token: data.accessToken || data.access_token,
            refresh_token: data.refreshToken || data.refresh_token,
            expires_in: data.expires_in || 300, // Default 5 minutes
            refresh_token_expires_in: data.refresh_token_expires_in || 31536000, // Default 1 year
            token_type: data.token_type || "Bearer",
            scope: data.scope || "openid offline_access",
          };

          // Preserve any additional fields
          if (data.id_token) transformedData.id_token = data.id_token;

          return new Response(JSON.stringify(transformedData), {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          });
        }
      } catch {
        // Return original response if parsing fails
      }
    }

    return response;
  };
}

function AuthWrapper({ children }) {
  const location = useLocation();

  const authConfig = useMemo(() => {
    const redirectUri = new URL(window.location.pathname, window.location.href)
      .href;

    return {
      clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
      authorizationEndpoint: `${process.env.REACT_APP_AUTH_DOMAIN}/authorize`,
      tokenEndpoint: `${process.env.REACT_APP_AUTH_DOMAIN}/token`,
      redirectUri,
      autoLogin: false,
      state: `state-${Date.now()}-${Math.random().toString(36).substring(7)}`,
      decodeToken: false, // Don't decode JWT, always use tokenExpiresIn
      tokenExpiresIn: 300, // 5 minutes - matches actual WorkOS token lifetime
      extraAuthParameters: {
        grantType: "pkce",
      },
      extraTokenParameters: {
        redirectUri,
        // Don't set grant_type here - our fetch interceptor handles it
      },
      // Request refresh tokens
      scope: "openid offline_access",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return <AuthProvider authConfig={authConfig}>{children}</AuthProvider>;
}

export default AuthWrapper;
